import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Chip,
  Divider,
  Tab,
  Tabs,
  Card,
  CardContent,
  IconButton,
  Alert
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Description,
  Work,
  LocationOn,
  AccountBalance,
  Phone,
  Email,
  Home
} from '@mui/icons-material';
import api from '../services/api';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const EmployeeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchEmployeeDetails();
  }, [id]);

  const fetchEmployeeDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/employees/${id}`);
      setEmployee(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching employee:', error);
      setError('Failed to load employee details');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading employee details...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error">{error}</Alert>
	<Button onClick={() => navigate('/admin/employees')} sx={{ mt: 2 }}>
          Back to Employees
        </Button>
      </Container>
    );
  }

  if (!employee) {
    return (
      <Container>
        <Typography>Employee not found</Typography>
	<Button onClick={() => navigate('/admin/employees')} sx={{ mt: 2 }}>
          Back to Employees
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
	<IconButton onClick={() => navigate('/admin/employees')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4">
            {employee.first_name} {employee.last_name}
          </Typography>
          <Chip 
            label={employee.status || 'active'} 
            color={employee.status === 'active' ? 'success' : 'default'}
            size="small"
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<Edit />}
	onClick={() => navigate(`/admin/employees/edit/${id}`)}
        >
          Edit Employee
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Personal Information" />
          <Tab label="Employment Details" />
          <Tab label="Documents" />
          <Tab label="Contracts" />
        </Tabs>
      </Paper>

      {/* Personal Information Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Personal Code
                    </Typography>
                    <Typography variant="body1">{employee.personal_code}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Date of Birth
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(employee.date_of_birth)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Nationality
                    </Typography>
                    <Typography variant="body1">{employee.nationality || 'LT'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Employment Type
                    </Typography>
                    <Typography variant="body1">
                      {employee.employment_type || 'Temporary'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Contact Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email fontSize="small" color="action" />
                    <Typography>{employee.email || 'Not provided'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Phone fontSize="small" color="action" />
                    <Typography>{employee.phone || 'Not provided'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Home fontSize="small" color="action" />
                    <Box>
                      <Typography>
                        {employee.address_street || 'Address not provided'}
                      </Typography>
                      {(employee.address_city || employee.address_postal_code) && (
                        <Typography variant="body2" color="text.secondary">
                          {employee.address_city} {employee.address_postal_code}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Emergency Contact */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Emergency Contact
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1">
                  {employee.emergency_contact_name || 'Not provided'}
                </Typography>
                {employee.emergency_contact_phone && (
                  <Typography variant="body2" color="text.secondary">
                    {employee.emergency_contact_phone}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Registration Info */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Registration Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Registration Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(employee.registration_date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Last Updated
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(employee.updated_at)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Employment Details Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Tax & Social Insurance
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      SODRA Code
                    </Typography>
                    <Typography variant="body1">
                      {employee.sodra_code || 'Not assigned'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Tax Code
                    </Typography>
                    <Typography variant="body1">
                      {employee.tax_code || 'Not assigned'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Banking Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Bank Account
                    </Typography>
                    <Typography variant="body1">
                      {employee.bank_account || 'Not provided'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Bank Name
                    </Typography>
                    <Typography variant="body1">
                      {employee.bank_name || 'Not provided'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Work Permits (for non-EU) */}
          {employee.work_permit_number && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Work Permit Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Permit Number
                      </Typography>
                      <Typography variant="body1">
                        {employee.work_permit_number}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Valid Until
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(employee.work_permit_valid_until)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Documents Tab */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Documents
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography color="text.secondary">
              Document management will be implemented in the next phase
            </Typography>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Contracts Tab */}
      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Employment Contracts
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography color="text.secondary">
              Contract management will be implemented in the next phase
            </Typography>
          </CardContent>
        </Card>
      </TabPanel>
    </Container>
  );
};

export default EmployeeDetail;
